import { IObservable, Subject } from '~/rage/utils';
import {
	CharacterSkill,
	ICharacterInitialClientData,
	IClientConfigs,
} from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import {
	ICharacterSkillLevel,
	ICharacterSkillsService,
} from './characterSkillsServiceTypes';

export class CharacterSkillsService implements ICharacterSkillsService {
	private maxExpBySkillLevel: Map<string, number> = new Map();
	private maxLevelBySkill: Map<CharacterSkill, number> = new Map();

	private skillLevels: Record<CharacterSkill, Subject<ICharacterSkillLevel>> =
		Object.values(CharacterSkill).reduce(
			(acc, skill) => {
				acc[skill] = new Subject<ICharacterSkillLevel>({
					level: 1,
					maxLevel: 1,
					exp: 0,
					maxExp: 0,
				});
				return acc;
			},
			{} as Record<CharacterSkill, Subject<ICharacterSkillLevel>>,
		);

	constructor(gameConfig: GameConfigService, backend: BackendService) {
		gameConfig.getConfigs().subscribe(this.handleGameConfigUpdate.bind(this));

		backend.addEventHandler(
			'character:init',
			this.handleCharacterInit.bind(this),
		);

		backend.addEventHandler('skills:update', this.handleSkillUpdate.bind(this));
	}

	private handleGameConfigUpdate(configs: Partial<IClientConfigs>): void {
		if (!configs.skills) {
			return;
		}

		const skillsConfig = configs.skills as {
			skills: Record<
				CharacterSkill,
				Array<{ level: number; expRequired: number }>
			>;
			maxLevelBySkill: Record<CharacterSkill, number>;
		};

		if (skillsConfig.maxLevelBySkill) {
			for (const [skill, maxLevel] of Object.entries(
				skillsConfig.maxLevelBySkill,
			)) {
				this.maxLevelBySkill.set(skill as CharacterSkill, maxLevel);
			}
		}

		if (!skillsConfig.skills) {
			return;
		}

		for (const skill of Object.values(CharacterSkill)) {
			const skillConfigs = skillsConfig.skills[skill as CharacterSkill];
			if (!skillConfigs) {
				continue;
			}

			for (const skillConfig of skillConfigs) {
				this.maxExpBySkillLevel.set(
					`${skill}_${skillConfig.level}`,
					skillConfig.expRequired,
				);
			}

			const skillLevel = this.skillLevels[skill].getValue();
			this.handleSkillUpdate({
				skill: skill as CharacterSkill,
				level: skillLevel.level,
				exp: skillLevel.exp,
			});
		}
	}

	private handleCharacterInit(payload: ICharacterInitialClientData) {
		for (const [skill, { level, exp }] of Object.entries(
			payload.skills ?? {},
		)) {
			this.handleSkillUpdate({
				skill: skill as CharacterSkill,
				level,
				exp,
			});
		}
	}

	private handleSkillUpdate(payload: {
		skill: CharacterSkill;
		level: number;
		exp: number;
	}) {
		const skillLevel = this.skillLevels[payload.skill];
		const maxExp = this.calculateNextLevelSkillExp(
			payload.skill,
			payload.level,
		);
		const maxLevel = this.calculateMaxLevelSkill(payload.skill);
		const newValue: ICharacterSkillLevel = {
			level: payload.level,
			maxLevel,
			exp: payload.exp,
			maxExp,
		};

		skillLevel.next(newValue);
	}

	private calculateNextLevelSkillExp(
		skill: CharacterSkill,
		level: number,
	): number {
		return this.maxExpBySkillLevel.get(`${skill}_${level + 1}`) ?? 0;
	}

	private calculateMaxLevelSkill(skill: CharacterSkill): number {
		return this.maxLevelBySkill.get(skill) ?? 1;
	}

	public getSkillLevel(
		skill: CharacterSkill,
	): IObservable<ICharacterSkillLevel> {
		return this.skillLevels[skill];
	}
}
